import { useMemo, useContext } from 'react'
import Link from 'next/link'
import _isEmpty from 'lodash/isEmpty'
import Image from 'next/legacy/image'
import classNames from 'classnames'
import { useSearchParams } from 'next/navigation'
import BackgroundHeaderArc from 'common/BackgroundHeaderArcNew'
import credentials from 'common/ProviderCredentials/credentials'
import { getProviderProfileRoute } from 'dataSource/ProviderSearch/routes'
import { formatProviderPronouns } from 'utils/formatProviderPronouns'
import { formatUserQuery, getStringifiedQuery } from 'utils/common'
import useBreakpoint from 'hooks/useBreakPoint'
import { DEFAULT_PROFILE_IMAGE_PATH } from 'common/ProviderPreview/constants'
import { getProviderTitle } from 'app/ProviderDetails/utils'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import { dateFormats } from 'constants/common'
import { getTimeZoneFormattedDate } from 'utils/common'
import { slotAvailabilityLabels } from 'app/Bookings/constants'
import {
  getSearchMatchSlotLocation,
  PROVIDER_PROFILE_SLOT_LOCATION_QUERY,
} from 'app/ProviderSearch/constants'
import styles from './index.module.scss'

type SlotGridHeaderProps = {
  providerDetails: Provider
  selectedSlot?: SelectedSlot
  appointmentMode?: string
  showShadow: boolean
  showSlotAndSessionInfo?: boolean
  showProviderCreds?: boolean
  showFloralsBehindTheImage: boolean
  slotLocation?: string
}

const SlotGridHeader: React.FC<SlotGridHeaderProps> = ({
  providerDetails,
  selectedSlot,
  appointmentMode = '',
  showShadow,
  showSlotAndSessionInfo = false,
  showProviderCreds = true,
  showFloralsBehindTheImage = false,
  slotLocation,
}) => {
  const {
    title,
    firstName,
    lastName,
    profileImagePath,
    pronouns,
    filters,
    practice,
  } = providerDetails
  const providerTitle = title || getProviderTitle(providerDetails)
  const providerCredentials = credentials[providerTitle] || {}
  const providerName = firstName + ' ' + lastName
  const providerProfileLink = getProviderProfileRoute(providerDetails)
  const searchParams = useSearchParams()

  // Create a query object from searchParams
  const query = Object.fromEntries(searchParams.entries())

  const formattedQuery = formatUserQuery(query)
  const searchMatchSlotLocation =
    getSearchMatchSlotLocation(slotLocation) ??
    getSearchMatchSlotLocation(query.slotLocation)
  const queryObject = {
    ...(query?.insuranceId && { insuranceId: query.insuranceId }),
    ...(practice?.distanceInMiles && {
      distanceInMiles: practice.distanceInMiles.toFixed(2),
    }),
    ...(!_isEmpty(formattedQuery?.counselingType) && {
      counselingType: formattedQuery.counselingType,
    }),
    ...(!_isEmpty(formattedQuery?.availabilityType) && {
      availabilityType: formattedQuery.availabilityType[0],
    }),
    ...(!_isEmpty(formattedQuery?.areaOfConcern) && {
      areaOfConcern: formattedQuery.areaOfConcern,
    }),
    ...(searchMatchSlotLocation && {
      [PROVIDER_PROFILE_SLOT_LOCATION_QUERY]: searchMatchSlotLocation,
    }),
  }
  const { isSmallSize } = useBreakpoint()

  // Display pronouns with slash between sets (e.g. She/Her/They/Them). Search uses pronouns[]; profile uses filters.
  const genderPronounValue = useMemo(() => {
    const fromPronouns = formatProviderPronouns(pronouns)
    const fromFilters = filters
      ?.filter((filter) => filter.type === 'gender_pronoun')
      ?.map((genderPronoun) => genderPronoun.value)
      ?.join('/')
    return fromPronouns || fromFilters
  }, [pronouns, filters])

  const { DAY_FORMAT, MONTH_DAY_FORMAT, TIME_WITH_TIMEZONE } = dateFormats
  const timeZoneFormattedDate = `${DAY_FORMAT} | ${MONTH_DAY_FORMAT} — ${TIME_WITH_TIMEZONE}`
  const { timeZone } = useContext(TimeZoneContext)

  return (
    <>
      <div tabIndex={-1} className={styles.SlotGridHeader}>
        <BackgroundHeaderArc showShadow={showShadow} />
        <Link
          href={{
            pathname: providerProfileLink,
            query: getStringifiedQuery(queryObject),
          }}
          className={styles.SlotGridHeader_linkContainer}
        >
          <div className={styles.SlotGridHeader_container}>
            <div className={styles.SlotGridHeader_container_image}>
              {showFloralsBehindTheImage ? (
                <>
                  <img
                    src="/images/provider-background-illustration-leaf.svg"
                    height={isSmallSize ? 87 : 166}
                    width={isSmallSize ? 126 : 180}
                    className={
                      styles.SlotGridHeader_container_image_providerBackgroundIllustration
                    }
                    alt="Provider background illustration"
                  />

                  <div
                    className={
                      styles.SlotGridHeader_container_image_subContainer
                    }
                  >
                    <img
                      src={
                        profileImagePath
                          ? profileImagePath
                          : DEFAULT_PROFILE_IMAGE_PATH
                      }
                      alt={`${providerName}'s profile` + ' image'}
                      className={
                        styles.SlotGridHeader_container_image_subContainer_providerImage
                      }
                    />
                  </div>
                </>
              ) : (
                <div
                  className={
                    styles.SlotGridHeader_container_image_subContainer_providerImage
                  }
                >
                  <Image
                    unoptimized={!!profileImagePath}
                    src={
                      profileImagePath
                        ? profileImagePath
                        : DEFAULT_PROFILE_IMAGE_PATH
                    }
                    layout="fill"
                    alt={`${providerName}'s profile image`}
                    className="rounded-circle"
                    sizes="100vw"
                    objectPosition="top"
                    objectFit="cover"
                  />
                </div>
              )}
            </div>

            <div
              className={classNames(styles.SlotGridHeader_container_text, {
                [styles.add_extra_margin]: showFloralsBehindTheImage,
              })}
            >
              <div
                className={styles.SlotGridHeader_container_text_subContainer}
              >
                <h3>
                  <span
                    className={
                      styles.SlotGridHeader_container_text_subContainer_name
                    }
                  >
                    {providerName}{' '}
                  </span>
                  {!!genderPronounValue && (
                    <span
                      className={
                        styles.SlotGridHeader_container_text_subContainer_pronouns
                      }
                    >
                      {genderPronounValue}
                    </span>
                  )}
                </h3>
              </div>
              {showSlotAndSessionInfo && (
                <div>
                  {selectedSlot?.slot && (
                    <div className="~text-sm ~font-medium ~text-black">
                      {getTimeZoneFormattedDate(
                        new Date(selectedSlot.slot.start),
                        timeZoneFormattedDate,
                        timeZone
                      )}
                    </div>
                  )}
                  <div className="~text-sm ~text-medium-grey">
                    {`Session with ${providerDetails.firstName} ${providerDetails.lastName}`}
                    {appointmentMode && (
                      <span> | {slotAvailabilityLabels[appointmentMode]}</span>
                    )}
                  </div>
                </div>
              )}
              {showProviderCreds && (
                <div>
                  <h2 className={styles.SlotGridHeader_container_text_title}>
                    {providerCredentials.definition}
                  </h2>
                </div>
              )}
            </div>
          </div>
        </Link>
      </div>
    </>
  )
}

export default SlotGridHeader
